/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

import { DEFAULT_COUNTRY } from './countries';
import { DEFAULT_EVENT_VISIBILITY } from './eventVisibility';
/**
 * The event wizard's whole form model, as a pure reducer.
 *
 * Kept free of React so the shape can be tested directly, and so an Edit screen
 * can hydrate the same state from an existing record and reuse every step
 * component unchanged.
 */

export const emptyRoute = () => ({
  start_point: { lat: '', lng: '' },
  start_address: '',
  end_point: { lat: '', lng: '' },
  end_address: '',
  total_distance: '',
  distance_measuring_unit_id: '',
  gpx_file_url: '',
  // GeoJSON LineString the event page draws; null until a course is drawn or
  // uploaded (see CourseField).
  route_polyline: null,
});

export const emptyVenue = () => ({
  venue_name: '',
  venue_address: '',
  venue_point: { lat: '', lng: '' },
});

export const emptyActivity = () => ({
  name: '',
  description: '',
  activity_type_id: '',
  location_model: 'ROUTE_BASED',
  max_participants: '',
  fee_amount: '0',
  route: emptyRoute(),
  venue: null,
  pit_stops: [],
});

export const emptySponsor = () => ({
  name: '', logo_url: '', cover_image: '', website_url: '',
  sponsor_label: '', sponsor_level: '',
});

export const emptyPitStop = () => ({
  // `address` is UI-only — the pit stop table stores name, description and
  // point, and nothing else. See the 'updatePitStopPlace' case below.
  name: '', description: '', address: '', point: { lat: '', lng: '' },
});

export const emptyPlatformRegistration = () => ({
  registration_start_datetime: '',
  registration_end_datetime: '',
  max_participants: '',
  // A Currency.id once chosen. Empty rather than defaulted to whatever the
  // catalog lists first: a currency nobody picked is a price in the wrong
  // money, and the column is nullable precisely so "not stated" stays
  // distinguishable from "stated as INR".
  currency_id: '',
});

export const emptyExternalRegistration = () => ({
  external_url: '',
  registration_start_date: '',
  registration_end_date: '',
});

export const initialEventForm = () => ({
  title: '',
  slug: '',
  description: '',
  prerequisites: '',
  preparation_steps: '',
  cover_image_url: '',
  official_website_url: '',
  creator_type: 'ORGANIZATION',
  creator_organization_id: '',
  organizer: 'PLATFORM',
  activity_mode: 'DEFAULT',
  visibility: DEFAULT_EVENT_VISIBILITY,
  location: '',
  location_state: '',
  // NOT a column. Holds the raw state of a row that could not be matched to
  // the picker's list, so the field can say what was there rather than appear
  // mysteriously blank. buildEventBatch writes only EVENT_COLUMNS, so this
  // cannot reach the database.
  location_state_unmatched: '',
  // Stated, not chosen: entries are taken in India only, so the field is
  // shown read-only and never starts blank.
  location_country: DEFAULT_COUNTRY,
  location_pincode: '',
  timezone: '',
  start_datetime: '',
  end_datetime: '',
  registration_source: 'PLATFORM',
  registration_type_id: '',
  is_registration_required: true,
  // Payments are deferred this phase; the flag stays false and the fee is
  // pinned to zero when the batch is built.
  is_payment_required: false,
  status: 'DRAFT',
  activities: [],
  sponsors: [],
  platform_registration: emptyPlatformRegistration(),
  external_registration: null,
});

/** Replaces one entry of a list without mutating the original. */
function replaceAt(list, index, updater) {
  return list.map((entry, position) => (position === index ? updater(entry) : entry));
}

/**
 * Registration groups follow the source. Holding on to a window the event no
 * longer uses would submit contradictory rows.
 *
 * D-54: `registration_source` says only WHERE a signup is taken — PLATFORM or
 * EXTERNAL. Whether one is taken at all is `is_registration_required`, and what
 * kind it is belongs to `registration_type_id`. The trailing branch is a guard
 * for an unset value, not a third source.
 */
function applyRegistrationSource(state, source) {
  if (source === 'PLATFORM') {
    return {
      ...state,
      registration_source: source,
      platform_registration: state.platform_registration || emptyPlatformRegistration(),
      external_registration: null,
    };
  }
  if (source === 'EXTERNAL') {
    return {
      ...state,
      registration_source: source,
      platform_registration: null,
      external_registration: state.external_registration || emptyExternalRegistration(),
    };
  }
  return {
    ...state,
    registration_source: source,
    platform_registration: null,
    external_registration: null,
  };
}

/**
 * An activity is route-based or venue-based, never both. Clearing the unused
 * side keeps the batch from carrying a half-filled child row.
 */
function applyLocationModel(activity, model) {
  if (model === 'ROUTE_BASED') {
    return { ...activity, location_model: model, route: activity.route || emptyRoute(), venue: null };
  }
  return {
    ...activity,
    location_model: model,
    route: null,
    venue: activity.venue || emptyVenue(),
    pit_stops: [],
  };
}

export function eventFormReducer(state, action) {
  switch (action.type) {
    case 'setField':
      if (action.field === 'registration_source') {
        return applyRegistrationSource(state, action.value);
      }
      return { ...state, [action.field]: action.value };

    case 'updateNested':
      return {
        ...state,
        [action.group]: { ...(state[action.group] || {}), [action.field]: action.value },
      };

    case 'addActivity':
      return { ...state, activities: [...state.activities, emptyActivity()] };

    case 'removeActivity':
      return {
        ...state,
        activities: state.activities.filter((_, position) => position !== action.index),
      };

    case 'updateActivity':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => (
          action.field === 'location_model'
            ? applyLocationModel(activity, action.value)
            : { ...activity, [action.field]: action.value }
        )),
      };

    case 'updateActivityNested':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          [action.group]: { ...(activity[action.group] || {}), [action.field]: action.value },
        })),
      };

    case 'updateActivityPoint':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          [action.group]: {
            ...(activity[action.group] || {}),
            [action.field]: { ...(activity[action.group]?.[action.field] || {}), [action.axis]: action.value },
          },
        })),
      };

    case 'addPitStop':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          pit_stops: [...(activity.pit_stops || []), emptyPitStop()],
        })),
      };

    case 'removePitStop':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          pit_stops: (activity.pit_stops || []).filter((_, position) => position !== action.stopIndex),
        })),
      };

    case 'updatePitStop':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          pit_stops: replaceAt(activity.pit_stops || [], action.stopIndex, (stop) => ({
            ...stop, [action.field]: action.value,
          })),
        })),
      };

    case 'updatePitStopPoint':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          pit_stops: replaceAt(activity.pit_stops || [], action.stopIndex, (stop) => ({
            ...stop, point: { ...stop.point, [action.axis]: action.value },
          })),
        })),
      };

    /**
     * A pit stop placed on the map: coordinates and the address they resolved to,
     * in one action so the two can never disagree.
     *
     * `address` is UI-only and is NOT saved. `event_activity_pit_stop` has just
     * name, description and point — no address column — so the text is a search
     * aid that helps somebody find the spot, and the coordinates are what the
     * stop actually is. Reopening a saved event shows the pin in the right place
     * with an empty address box, which is the honest rendering of what is stored.
     */
    case 'updatePitStopPlace':
      return {
        ...state,
        activities: replaceAt(state.activities, action.index, (activity) => ({
          ...activity,
          pit_stops: replaceAt(activity.pit_stops || [], action.stopIndex, (stop) => ({
            ...stop,
            point: { lat: action.lat ?? '', lng: action.lng ?? '' },
            // Only overwritten when a lookup actually produced one, so a failed
            // reverse geocode moves the pin without wiping the typed text.
            address: action.address !== undefined ? action.address : stop.address,
          })),
        })),
      };

    case 'addSponsor':
      return { ...state, sponsors: [...state.sponsors, emptySponsor()] };

    case 'removeSponsor':
      return {
        ...state,
        sponsors: state.sponsors.filter((_, position) => position !== action.index),
      };

    case 'updateSponsor':
      return {
        ...state,
        sponsors: replaceAt(state.sponsors, action.index, (sponsor) => ({
          ...sponsor, [action.field]: action.value,
        })),
      };

    case 'hydrate':
      return { ...initialEventForm(), ...action.value };

    default:
      return state;
  }
}
